import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, ChevronLeft, ChevronRight, Loader2, AlertCircle, FileSpreadsheet, FileText } from 'lucide-react'
import { format, parseISO, addDays } from 'date-fns'
import { getInOutDay, inOutExportUrl, type InOutReservation } from '../../lib/api/client'
import { cn } from '../../lib/cn'

const TABS = [
  { key: 'in', label: 'Arrivi' },
  { key: 'out', label: 'Partenze' },
  { key: 'stay', label: 'In garage' },
] as const
type TabKey = (typeof TABS)[number]['key']

const hhmm = (t: string) => (t || '').slice(0, 5)
const ddmm = (d: string) => {
  try {
    return format(parseISO(d), 'dd/MM')
  } catch {
    return d
  }
}
function sourceChip(src: string) {
  return (src || '').toUpperCase() || 'DIRETTO'
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-card border border-line bg-card px-3.5 py-2.5">
      <div className="text-[10.5px] font-bold uppercase tracking-[1.2px] text-muted">{label}</div>
      <div className={cn('mt-0.5 font-display text-[22px] font-bold leading-none tnum', tone ?? 'text-coal')}>{value}</div>
    </div>
  )
}

function ResvRow({ r, kind }: { r: InOutReservation; kind: TabKey }) {
  const time = kind === 'out' ? hhmm(r.leaveHour) : hhmm(r.arrivalHour)
  return (
    <div className="flex items-center gap-3 border-b border-line-soft px-1 py-2.5 last:border-0">
      <span
        className={cn(
          'w-12 shrink-0 font-mono text-[15px] font-bold tnum',
          kind === 'out' ? 'text-canal' : kind === 'in' ? 'text-ok' : 'text-slate',
        )}
      >
        {kind === 'stay' ? <span className="text-[12px] text-muted">in sosta</span> : time}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-coal">
          {r.name} {r.lastName}
        </div>
        <div className="truncate text-[12px] text-muted">
          <span className="font-mono tracking-wide text-coal">{r.licencePlate || '—'}</span>
          {' · '}
          {r.transport || '—'}
          {kind === 'stay' && (
            <>
              {' · '}
              <span className="font-mono">
                {ddmm(r.arrivalDay)} {hhmm(r.arrivalHour)} → {ddmm(r.leaveDay)} {hhmm(r.leaveHour)}
              </span>
            </>
          )}
        </div>
      </div>
      <span className="hidden font-mono text-[11px] text-muted sm:inline">#{r.code}</span>
      <span className="gv-src border-line bg-paper text-slate">{sourceChip(r.source)}</span>
    </div>
  )
}

export function InOutDayModal({
  date,
  onClose,
  onNavigate,
}: {
  date: string | null
  onClose: () => void
  onNavigate: (date: string) => void
}) {
  const [tab, setTab] = useState<TabKey>('in')

  const day = useQuery({
    queryKey: ['inOutDay', date],
    queryFn: () => getInOutDay(date!),
    enabled: Boolean(date),
  })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (date) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [date, onClose])

  if (!date) return null
  const d = day.data
  const rows: Record<TabKey, InOutReservation[]> = {
    in: d?.inReservations ?? [],
    out: d?.outReservations ?? [],
    stay: d?.sedentaryReservations ?? [],
  }
  const counts = { in: rows.in.length, out: rows.out.length, stay: rows.stay.length }
  const headDate = format(parseISO(date), 'EEEE dd/MM/yyyy')
  const pdfFilter: 'inout' | 'sedentary' = tab === 'stay' ? 'sedentary' : 'inout'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px] animate-[fadeIn_.15s_ease]" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-card border border-line bg-paper shadow-pop">
        {/* header */}
        <header className="flex items-center gap-3 border-b border-line bg-card px-5 py-3.5">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onNavigate(format(addDays(parseISO(date), -1), 'yyyy-MM-dd'))}
              className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal"
              title="Giorno precedente"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[160px] text-center font-display text-[15px] font-bold capitalize text-coal">{headDate}</span>
            <button
              onClick={() => onNavigate(format(addDays(parseISO(date), 1), 'yyyy-MM-dd'))}
              className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal"
              title="Giorno successivo"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <a
              href={inOutExportUrl(date, 'excel')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12.5px] font-semibold text-slate hover:border-faint"
              title="Esporta Excel"
            >
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </a>
            <a
              href={inOutExportUrl(date, 'pdf', pdfFilter)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12.5px] font-semibold text-slate hover:border-faint"
              title="Esporta PDF"
            >
              <FileText className="h-4 w-4" /> PDF
            </a>
            <button onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-paper hover:text-coal">
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>

        {day.isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : day.isError ? (
          <div className="py-20 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il giorno.
          </div>
        ) : (
          <>
            {/* stats + time-slot strip */}
            <div className="border-b border-line bg-card-2 px-5 py-4">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                <Stat label="In garage" value={d?.inGarage ?? 0} tone="text-coal" />
                <Stat label="Stanziali" value={d?.sedentary ?? 0} tone="text-slate" />
                <Stat label="Arrivi" value={counts.in} tone="text-ok" />
                <Stat label="Partenze" value={counts.out} tone="text-canal" />
              </div>
              {counts.in + counts.out > 0 && (
                <div className="mt-3 grid grid-cols-5 gap-1.5">
                  {d!.ranges.map((rg, i) => (
                    <div key={i} className="rounded-md border border-line bg-card px-2 py-1.5 text-center">
                      <div className="text-[10.5px] font-semibold text-muted">
                        {hhmm(rg.startHour)}–{hhmm(rg.endHour)}
                      </div>
                      <div className="mt-0.5 flex items-center justify-center gap-2 font-mono text-[12px] font-bold tnum">
                        <span className={rg.in > 0 ? 'text-ok' : 'text-faint'}>+{rg.in}</span>
                        <span className={rg.out > 0 ? 'text-canal' : 'text-faint'}>−{rg.out}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* tabs */}
            <div className="flex gap-1 border-b border-line bg-card px-4">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-semibold transition-colors',
                    tab === t.key ? 'border-verdigris text-coal' : 'border-transparent text-muted hover:text-coal',
                  )}
                >
                  {t.label}
                  <span className="rounded-full bg-paper px-1.5 text-[11px] tnum text-muted ring-1 ring-line">{counts[t.key]}</span>
                </button>
              ))}
            </div>

            {/* list */}
            <div className="flex-1 overflow-y-auto px-5 py-2">
              {rows[tab].length === 0 ? (
                <p className="py-14 text-center text-sm text-muted">
                  {tab === 'in' ? 'Nessun arrivo' : tab === 'out' ? 'Nessuna partenza' : 'Nessuna prenotazione in sosta'} in questa giornata.
                </p>
              ) : (
                rows[tab].map((r, i) => <ResvRow key={`${r.code}-${i}`} r={r} kind={tab} />)
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
